import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';

import { INITIAL_FILTERS, INITIAL_HEALTH_CASE } from './constants';
import {
  DeleteHealthCaseProps,
  HealthCasesFilters,
  HealthCasesState,
  SaveHealthCaseProps,
  SetFiltersProps,
} from './types';

import { notifyActions } from '../notifications/slice';
import { resultsActions } from '../results/slice';

import {
  BaseResult,
  BatchRequestUUID,
  HealthCase,
  HealthCaseCreateRequest,
  HealthCaseObjectType,
  HealthCaseUpdateRequest,
  ResultType,
} from '@/api/__generated__/webApi';
import { FetchHealthCasesResponse, healthCasesApi } from '@/api/healthCases';
import { getTranslate } from '@/i18n/getTranslate';
import i18n from '@/i18n/i18n';
import { DEFAULT_PER_PAGE } from '@/shared/constants';
import { envs } from '@/shared/constants/envs';
import { RootState } from '@/store';
import { getError } from '@/store/common/error-handlers';
import { errorHandler } from '@/store/errorHandler';
import { storageActions } from '@/store/storage/slice';

const initialState: HealthCasesState = {
  cases: {
    items: [],
    filters: INITIAL_FILTERS,
    hasNext: true,
  },
  isLoading: false,
  isInitialLoading: true,
  isSaving: false,
  result: [],
  lastCreatedCase: '',
  selectedResults: new Set<string>(),
  savedResults: new Set<string>(),
  initialResults: new Set<string>(),
  selectedDocuments: new Set<string>(),
  savedDocuments: new Set<string>(),
  initialDocuments: new Set<string>(),
  removeItems: [] as string[],
};

enableMapSet();
export const fetchHealthCasesWithFilters = createAsyncThunk<
  { data: FetchHealthCasesResponse; filters: HealthCasesFilters },
  Omit<HealthCasesFilters, 'perPage'>,
  {
    state: RootState;
  }
>(
  'healthCases/fetchHealthCases',
  async (params, { rejectWithValue, signal }) => {
    try {
      const filters: HealthCasesFilters = {
        perPage: DEFAULT_PER_PAGE,
        ...params,
      };

      return {
        data: await healthCasesApi.getHealthCases({
          signal,
          ...filters,
        }),
        filters,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const addHealthCase = createAsyncThunk(
  'healthCases/addHealthCase',
  async (payload: HealthCaseCreateRequest, { rejectWithValue }) => {
    try {
      return await healthCasesApi.createHealthCase(payload);
    } catch (e) {
      const error = getError(e);
      errorHandler(error); // todo [tech-debt] - this is saga error handler and it just dont works here
      return rejectWithValue(error);
    }
  }
);

export const updateHealthCase = createAsyncThunk(
  'healthCases/updateHealthCase',
  async (
    payload: { id: HealthCase['id']; data: HealthCaseUpdateRequest },
    { rejectWithValue }
  ) => {
    try {
      return await healthCasesApi.updateHealthCase(payload.id, payload.data);
    } catch (e) {
      const error = getError(e);
      errorHandler(error);
      return rejectWithValue(error);
    }
  }
);

export const saveHealthCase = createAsyncThunk(
  'healthCases/saveHealthCase',
  async (
    payload: SaveHealthCaseProps & { isDetails?: boolean },
    { rejectWithValue, dispatch, getState }
  ) => {
    const isEdit = !!payload.id;
    const isDetails = payload.isDetails;

    let currentHealthCase = INITIAL_HEALTH_CASE;

    try {
      const state = getState();
      const {
        healthCases: {
          cases: { items: healthCasesItems },
        },
        storage: {
          files: { items: documentsItems },
        },
        results: { results: resultsItems },
      } = state as RootState;

      if (isEdit) {
        const updatedHCase = healthCasesItems.find(
          (hCase) => hCase.id === payload.id
        );

        if (updatedHCase?.id) {
          currentHealthCase = updatedHCase;
        }
      }

      if (!isEdit) {
        currentHealthCase = await dispatch(
          addHealthCase(payload.data)
        ).unwrap();
      }
      const results = resultsItems?.byID
        ? Object.values(resultsItems.byID)
        : [];

      const { laboratoryResults, manualResults } =
        payload.savedIdsResults.reduce(
          (acc, resultId) => {
            const result = results.find(
              (resultsItems) => resultsItems?.id === resultId
            );
            const isHCDuplicate = result?.healthCases?.some(
              (resultHCase) => resultHCase.id === currentHealthCase.id
            );
            if (!isHCDuplicate) {
              if (result?.type === ResultType.MANUAL) {
                acc.manualResults.push(resultId);
              } else {
                acc.laboratoryResults.push(resultId);
              }
            }

            return acc;
          },
          { laboratoryResults: [] as string[], manualResults: [] as string[] }
        );

      const addResultsPromises = laboratoryResults.map((resultId) =>
        healthCasesApi
          .addResultToHealthCase({
            resultId,
            healthCaseId: payload.id || currentHealthCase.id,
          })
          .then(() => {
            dispatch(
              storageActions.addHealthCase({
                id: resultId,
                healthCase: currentHealthCase,
              })
            );
            dispatch(
              resultsActions.addHealthCase({
                id: resultId,
                healthCase: currentHealthCase,
              })
            );
          })
          .catch((e: unknown) => {
            throw new Error('health case not added to result', { cause: e });
          })
      );

      const filteredDocumentsIds = payload.savedIdsDocuments
        .filter((documentId) => {
          const document = documentsItems.find(
            (documentItem) => documentItem.id === documentId
          );
          return !document?.healthCases.some(
            (documentHCase) => documentHCase.id === currentHealthCase.id
          );
        })
        .concat(manualResults);

      const addDocumentsPromises = filteredDocumentsIds.map((documentId) =>
        healthCasesApi
          .addDocumentToHealthCase({
            storageId: documentId,
            healthCaseId: currentHealthCase.id,
          })
          .then(() => {
            dispatch(
              storageActions.addHealthCase({
                id: documentId,
                healthCase: currentHealthCase,
              })
            );
          })
          .catch((e: unknown) => {
            console.error(e);
          })
      );

      if (!isDetails) {
        dispatch(healthCasesActions.clearSelectedDocuments());
        dispatch(healthCasesActions.clearSelectedResults());
      }

      const { deletedAttachmentsPromises, deletedResultsIds } = (
        payload.removeItems || []
      ).reduce<{
        deletedAttachmentsPromises: Promise<number>[];
        deletedResultsIds: string[];
        deletedDocumentsIds: string[];
      }>(
        (acc, object) => {
          acc.deletedAttachmentsPromises.push(
            healthCasesApi.deleteHealthCaseFile(object.id)
          );

          if (object.value) {
            if (object.type === HealthCaseObjectType.RESULT) {
              acc.deletedResultsIds.push(object.value);
            }
            if (object.type === HealthCaseObjectType.STORAGE) {
              acc.deletedDocumentsIds.push(object.value);
            }
          }

          return acc;
        },
        {
          deletedAttachmentsPromises: [],
          deletedResultsIds: [],
          deletedDocumentsIds: [],
        }
      );

      await Promise.all([
        ...addResultsPromises,
        ...addDocumentsPromises,
        ...deletedAttachmentsPromises,
      ]);

      deletedResultsIds.map((result) =>
        dispatch(
          resultsActions.removeHealthCase({
            id: result,
            healthCaseId: currentHealthCase.id,
          })
        )
      );

      if (manualResults.length) {
        manualResults.forEach((resultId) => {
          dispatch(
            resultsActions.addHealthCase({
              id: resultId,
              healthCase: currentHealthCase,
            })
          );
        });
      }

      if (isEdit && currentHealthCase.id) {
        await dispatch(
          updateHealthCase({ id: currentHealthCase.id, data: payload.data })
        ).unwrap();
      }

      return currentHealthCase.id;
    } catch (e) {
      console.error(e);
      return rejectWithValue(e);
    }
  }
);

export const deleteHealthCase = createAsyncThunk(
  'storage/deleteHealthCase',
  async (payload: DeleteHealthCaseProps, { rejectWithValue, dispatch }) => {
    const t = await getTranslate('healthCases');
    try {
      await healthCasesApi.deleteHealthCase(payload.id);
      dispatch(
        notifyActions.showNotifications([
          {
            id: payload.id,
            type: 'success',
            text: t('HEALTH_CASE_DELETE_SUCCESS'),
          },
        ])
      );
      return payload.id;
    } catch (e) {
      dispatch(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
      const error = getError(e);
      return rejectWithValue(error);
    }
  }
);

export const removeHealthCaseFromResult = createAsyncThunk(
  'result/removeHealthCase',
  async (
    payload: { resultId: string; requests: BatchRequestUUID['requests'] },
    { rejectWithValue, dispatch }
  ) => {
    const { resultId, requests } = payload;
    try {
      await healthCasesApi.removeHealthCaseFromResult(resultId, requests);
      dispatch(
        notifyActions.showNotifications([
          {
            id: resultId,
            type: 'success',
            text: i18n.t('HEALTH_CASE_DELETE_SUCCESS'),
          },
        ])
      );
      return resultId;
    } catch (e) {
      dispatch(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: i18n.t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
      const error = getError(e);
      return rejectWithValue(error);
    }
  }
);

export const healthCases = createSlice({
  name: 'healthCases',
  initialState,
  reducers: {
    setFilters: (state, { payload }: PayloadAction<SetFiltersProps>) => {
      if (payload.reset) {
        state.cases.filters = payload.newState;
      } else {
        state.cases.filters = { ...state.cases.filters, ...payload.newState };
      }
    },
    toggleResultsSelection: (state, { payload }: PayloadAction<string>) => {
      if (state.selectedResults.has(payload)) {
        state.selectedResults.delete(payload);
      } else {
        state.selectedResults.add(payload);
      }
    },
    clearFilters: (state) => {
      state.cases.filters = INITIAL_FILTERS;
    },
    setResult: (state, { payload }: PayloadAction<BaseResult[]>) => {
      state.result = payload;
    },
    saveSelectedResults: (state) => {
      state.selectedResults.forEach((result) => {
        state.savedResults.add(result);

        const index = state.removeItems.indexOf(result);
        if (index !== -1) {
          state.removeItems.splice(index, 1);
        }
      });
    },
    initialSelectedResults: (
      state,
      { payload }: PayloadAction<Set<string>>
    ) => {
      state.initialResults = payload;
    },
    setSelectedResults: (state, { payload }: PayloadAction<string[]>) => {
      state.selectedResults = new Set([...state.selectedResults, ...payload]);
    },
    clearSelectedResults: (state) => {
      state.selectedResults.clear();
    },
    clearSavedResults: (state) => {
      state.savedResults.clear();
    },
    toggleDocumentsSelection: (state, { payload }: PayloadAction<string>) => {
      if (state.selectedDocuments.has(payload)) {
        state.selectedDocuments.delete(payload);
      } else {
        state.selectedDocuments.add(payload);
      }
    },
    saveSelectedDocuments: (state) => {
      state.selectedDocuments.forEach((file) => {
        state.savedDocuments.add(file);

        const index = state.removeItems.indexOf(file);
        if (index !== -1) {
          state.removeItems.splice(index, 1);
        }
      });
    },
    initialSelectedDocuments: (
      state,
      { payload }: PayloadAction<Set<string>>
    ) => {
      state.initialDocuments = payload;
    },
    clearSelectedDocuments: (state) => {
      state.selectedDocuments.clear();
    },
    clearSavedDocuments: (state) => {
      state.savedDocuments.clear();
    },
    removeDocument: (state, { payload }: PayloadAction<string>) => {
      state.savedDocuments.delete(payload);
      state.initialDocuments.delete(payload);
      state.removeItems.push(payload);
    },
    removeResults: (state, { payload }: PayloadAction<string>) => {
      state.savedResults.delete(payload);
      state.initialResults.delete(payload);
      state.removeItems.push(payload);
    },
    clearRemovedItems: (state) => {
      state.removeItems = [];
    },
    clearLastCreatedCase: (state) => {
      state.lastCreatedCase = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHealthCasesWithFilters.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchHealthCasesWithFilters.fulfilled, (state, action) => {
      const { data, filters } = action.payload;
      state.cases =
        filters.startPage !== 0
          ? {
              ...state.cases,
              hasNext: data.hasNext,
              items: [...state.cases.items, ...data.items],
              filters,
            }
          : { ...data, filters };
      state.isLoading = false;
      state.isInitialLoading = false;
    });
    builder.addCase(updateHealthCase.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateHealthCase.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateHealthCase.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(saveHealthCase.pending, (state) => {
      state.isSaving = true;
    });
    builder.addCase(saveHealthCase.fulfilled, (state) => {
      state.isSaving = false;
    });
    builder.addCase(saveHealthCase.rejected, (state) => {
      state.isSaving = false;
    });
    builder.addCase(addHealthCase.fulfilled, (state, { payload }) => {
      state.cases.items = [{ ...payload }, ...state.cases.items];
      state.lastCreatedCase = payload.id;
    });
  },
});

export const healthCasesReducer = healthCases.reducer;
export const healthCasesActions = healthCases.actions;
export const healthCasesStoreName = healthCases.name;
